import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Une campagne de relance = un destinataire. */
export interface AprilRelance {
  id: number;
  user_id?: number | null;
  recipient_email: string;
  recipient_name?: string | null;
  subject?: string | null;
  status: string; // active | stopped | finished | failed
  sends_done: number;
  max_sends: number;
  original_sent_at?: string | null;
  campaign_starts_at?: string | null;
  /** Prochain creneau ou la relance partira (null si stoppee/terminee). */
  next_send_at?: string | null;
  campaign_ends_at?: string | null;
  last_sent_at?: string | null;
  last_error?: string | null;
  stopped_at?: string | null;
}

export interface AprilRelanceListResponse {
  items: AprilRelance[];
  total: number;
  active_count: number;
  stopped_count: number;
  finished_count: number;
  failed_count: number;
  subject: string;
  weeks: number;
  max_sends: number;
  /** Mise en route : les mails APRIL antérieurs ne sont jamais relancés. */
  activation_at?: string | null;
}

export interface AprilRelanceSyncResult {
  scanned_accounts: number;
  enrolled: number;
  skipped: number;
  /** Mails écartés car envoyés avant la mise en route. */
  ignored_before_activation: number;
  activation_at?: string | null;
  errors: string[];
}

@Injectable({
  providedIn: 'root'
})
export class RelancesAprilService {
  private apiUrl = `${environment.apiUrl}${environment.apiEndpoint}/relances-april`;

  constructor(private http: HttpClient) {}

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('access_token');
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  list(
    status?: string | null,
    search?: string | null,
    limit: number = 200,
    offset: number = 0
  ): Observable<AprilRelanceListResponse> {
    let params = new HttpParams()
      .set('limit', String(limit))
      .set('offset', String(offset));

    if (status) {
      params = params.set('status', status);
    }
    if (search?.trim()) {
      params = params.set('search', search.trim());
    }

    return this.http.get<AprilRelanceListResponse>(this.apiUrl, {
      headers: this.getAuthHeaders(),
      params
    });
  }

  /** Scanne les « Envoyés » Gmail et inscrit les nouveaux destinataires. */
  sync(lookbackDays?: number | null): Observable<AprilRelanceSyncResult> {
    let params = new HttpParams();
    if (lookbackDays) {
      params = params.set('lookback_days', String(lookbackDays));
    }

    return this.http.post<AprilRelanceSyncResult>(
      `${this.apiUrl}/sync`,
      {},
      { headers: this.getAuthHeaders(), params }
    );
  }

  /** Coupe les relances de ce destinataire uniquement. */
  stop(relanceId: number): Observable<AprilRelance> {
    return this.http.post<AprilRelance>(
      `${this.apiUrl}/${relanceId}/stop`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }

  /** Réactive une série stoppée. */
  resume(relanceId: number): Observable<AprilRelance> {
    return this.http.post<AprilRelance>(
      `${this.apiUrl}/${relanceId}/resume`,
      {},
      { headers: this.getAuthHeaders() }
    );
  }
}
